<script setup lang="ts">
import { nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import ChevronDown from 'lucide-vue-next/dist/esm/icons/chevron-down.js';

const props = withDefaults(
  defineProps<{
    /** Stick to bottom when message count / fingerprint changes. */
    stickKey?: string | number;
    /** Constrain message column width (agent-style). 0 = full bleed. */
    maxWidth?: number;
    /** Auto-scroll when stickKey changes (default true). */
    autoScroll?: boolean;
    /**
     * Distance from the true bottom (px) that still counts as “near end”.
     * Manual scroll beyond this pauses auto-stick; scrolling back within it
     * re-enables it. Default 96 ≈ a bit under one message row.
     */
    nearBottomPx?: number;
    /** Show a jump-to-bottom chip when the user has scrolled up. */
    showJumpButton?: boolean;
  }>(),
  {
    maxWidth: 0,
    autoScroll: true,
    nearBottomPx: 96,
    showJumpButton: true,
  }
);

const scroller = ref<HTMLElement | null>(null);
const inner = ref<HTMLElement | null>(null);
/** User wants to follow the live tail (Teams/Slack stick-to-bottom). */
const stickToBottom = ref(true);
const showScrollButton = ref(false);

/** Ignore scroll events caused by our own scrollTop writes. */
let ignoreScrollUntil = 0;
let resizeObserver: ResizeObserver | null = null;
let pendingScroll = false;

function distanceFromBottom(el: HTMLElement): number {
  return el.scrollHeight - el.scrollTop - el.clientHeight;
}

function isNearBottom(el: HTMLElement): boolean {
  // Not % of document length — that makes long chats almost impossible to
  // unstick (5% of a long history is hundreds of px). Pixel slack only.
  return distanceFromBottom(el) <= props.nearBottomPx;
}

function pinToBottom() {
  const el = scroller.value;
  if (!el) return;
  // Two frames: first after Vue DOM, second after layout (markdown, images).
  ignoreScrollUntil = performance.now() + 120;
  el.scrollTop = el.scrollHeight;
  requestAnimationFrame(() => {
    if (!scroller.value) return;
    scroller.value.scrollTop = scroller.value.scrollHeight;
    ignoreScrollUntil = performance.now() + 80;
    stickToBottom.value = true;
    showScrollButton.value = false;
  });
}

function scrollToBottom(force = false) {
  if (!force && props.autoScroll === false) return;
  if (!force && !stickToBottom.value) {
    showScrollButton.value = true;
    return;
  }
  if (pendingScroll) return;
  pendingScroll = true;
  nextTick(() => {
    pendingScroll = false;
    if (!force && !stickToBottom.value) {
      showScrollButton.value = true;
      return;
    }
    pinToBottom();
  });
}

function onScroll() {
  if (performance.now() < ignoreScrollUntil) return;
  const el = scroller.value;
  if (!el) return;
  if (isNearBottom(el)) {
    stickToBottom.value = true;
    showScrollButton.value = false;
  } else {
    // Any intentional leave of the bottom zone pauses auto-follow.
    stickToBottom.value = false;
    showScrollButton.value = true;
  }
}

function onContentResize() {
  if (props.autoScroll === false) return;
  if (!stickToBottom.value) {
    // Still update jump chip if content grew under a paused user.
    const el = scroller.value;
    if (el) showScrollButton.value = !isNearBottom(el);
    return;
  }
  pinToBottom();
}

watch(
  () => props.stickKey,
  () => {
    if (props.autoScroll === false) return;
    scrollToBottom(false);
  }
);

onMounted(() => {
  if (props.autoScroll !== false) scrollToBottom(true);

  // Content height changes (stream deltas, markdown, images) without a
  // stickKey tick still need to keep the pin if the user is following.
  if (typeof ResizeObserver !== 'undefined' && inner.value) {
    resizeObserver = new ResizeObserver(() => onContentResize());
    resizeObserver.observe(inner.value);
  }
});

onBeforeUnmount(() => {
  resizeObserver?.disconnect();
  resizeObserver = null;
});

defineExpose({ scrollToBottom, scroller, stickToBottom });
</script>

<template>
  <div
    ref="scroller"
    class="nx-chat-thread"
    data-testid="nx-chat-thread"
    @scroll.passive="onScroll"
  >
    <div
      ref="inner"
      class="nx-chat-thread__inner"
      :style="maxWidth > 0 ? { maxWidth: `${maxWidth}px` } : undefined"
    >
      <slot />
      <!-- Anchor so scrollHeight always has a pin point past the last message. -->
      <div class="nx-chat-thread__anchor" aria-hidden="true" />
    </div>

    <Transition name="nx-chat-thread-fade">
      <button
        v-if="showJumpButton && showScrollButton"
        type="button"
        class="nx-chat-thread__jump"
        data-testid="nx-chat-thread-jump"
        aria-label="Scroll to bottom"
        @click="scrollToBottom(true)"
      >
        <ChevronDown class="nxt-icon-lg" />
      </button>
    </Transition>
  </div>
</template>

<style scoped>
.nx-chat-thread {
  flex: 1 1 auto;
  min-height: 0;
  overflow-x: hidden;
  overflow-y: auto;
  padding: var(--nxt-space-sm) 0 var(--nxt-space-md);
  position: relative;
  display: flex;
  flex-direction: column;
  background:
    linear-gradient(
      180deg,
      color-mix(in srgb, var(--nxt-brand-bg) 5%, transparent) 0%,
      transparent calc(var(--nxt-size-unit) * 3)
    ),
    var(--nxt-surface-bg);
}
.nx-chat-thread__inner {
  width: 100%;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  box-sizing: border-box;
  padding: 0 var(--nxt-space-2xs);
  flex: 0 0 auto;
}
.nx-chat-thread__anchor {
  height: var(--nxt-hairline);
  flex-shrink: 0;
}
/* Overlay — must not sit in document flow or scrollHeight jumps when it mounts. */
.nx-chat-thread__jump {
  position: absolute;
  bottom: var(--nxt-space-md);
  left: 50%;
  transform: translateX(-50%);
  width: var(--nxt-control-size-md);
  height: var(--nxt-control-size-md);
  border-radius: 50%;
  background: var(--nxt-surface-bg);
  border: var(--nxt-hairline) solid color-mix(in srgb, var(--nxt-brand-bg) 15%, transparent);
  box-shadow: var(--nxt-shadow-md);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--nxt-brand-bg);
  z-index: var(--nxt-z-float);
  transition:
    transform var(--nxt-motion-fast) ease,
    box-shadow var(--nxt-motion-fast) ease,
    background var(--nxt-motion-fast) ease,
    color var(--nxt-motion-fast) ease;
}
.nx-chat-thread__jump:hover {
  background: var(--nxt-brand-bg);
  color: var(--nxt-brand-fg);
  border-color: transparent;
  transform: translateX(-50%) translateY(calc(var(--nxt-size-unit) * -0.0625));
  box-shadow: var(--nxt-shadow-lg);
}
.nx-chat-thread-fade-enter-active,
.nx-chat-thread-fade-leave-active {
  transition: opacity var(--nxt-motion-base);
}
.nx-chat-thread-fade-enter-from,
.nx-chat-thread-fade-leave-to {
  opacity: 0;
}
</style>
